import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Factory, Award, Truck, Shield, Phone, Mail, MapPin, Star, Zap, Package, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BestDataCableManufacturerIndia = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    { icon: Factory, title: '50,000+ Daily Production', desc: 'State-of-the-art manufacturing facility in Noida' },
    { icon: Award, title: 'ISO 9001:2015 Certified', desc: 'International quality management standards' },
    { icon: Shield, title: 'BIS Compliant', desc: 'Bureau of Indian Standards certified products' },
    { icon: Truck, title: 'Pan India Delivery', desc: 'Fast shipping to all states within 3-5 days' },
    { icon: Users, title: '500+ Dealers', desc: 'Trusted dealer network across India' },
    { icon: Zap, title: '45W-120W Support', desc: 'All fast charging protocols supported' }
  ];

  const cableTypes = [
    { name: 'USB to Type-C Cables', power: '45W-120W', features: ['VOOC Support', 'Super Fast Charging', 'Braided/Silicone'] },
    { name: 'USB to Micro USB Cables', power: '2.4A-3A', features: ['Quick Charge', 'Durable Build', 'Multiple Lengths'] },
    { name: 'Type-C to Type-C Cables', power: '60W-100W PD', features: ['Power Delivery', 'Data Transfer', 'Premium Quality'] },
    { name: 'Type-C to Lightning Cables', power: '20W PD', features: ['iPhone Compatible', 'Fast Charging', 'MFi Design'] }
  ];

  const faqs = [
    {
      q: 'Who is the best data cable manufacturer in India?',
      a: 'RSD Accessories Pvt Ltd is recognized as one of India\'s leading data cable manufacturers. Based in Noida, Delhi NCR, RSD has been manufacturing premium quality USB cables, Type-C cables, and fast charging cables since 2016. With ISO 9001:2015 certification, 50,000+ daily production capacity, and a network of 500+ dealers across India, RSD Accessories is the trusted choice for distributors, wholesalers, and OEM partners.'
    },
    {
      q: 'What types of data cables does RSD manufacture?',
      a: 'RSD manufactures a comprehensive range of data cables including: USB to Type-C cables (45W-120W), USB to Micro USB cables, Type-C to Type-C PD cables (60W-100W), and Type-C to Lightning cables (20W PD). Our popular series include DT-13, DT-15, and DT-21 with options in silicone, braided nylon, and premium metal connector variants.'
    },
    {
      q: 'What is the minimum order quantity for bulk data cables?',
      a: 'Our minimum order quantity (MOQ) typically starts from 5,000-10,000 units depending on the product category and customization requirements. For new business partners, we offer flexible MOQ options. Volume discounts are available for orders above 25,000 units.'
    },
    {
      q: 'Does RSD provide OEM data cable manufacturing?',
      a: 'Yes, RSD Accessories provides complete OEM and ODM services for data cables. We offer custom branding, logo printing/embossing, custom color options, private label packaging, and exclusive product designs. Our OEM services have helped 100+ brands launch their own cable product lines.'
    },
    {
      q: 'What fast charging protocols do RSD cables support?',
      a: 'RSD data cables support all major fast charging protocols including Qualcomm Quick Charge (2.0/3.0/4.0/5.0), USB Power Delivery (PD), VOOC/Super VOOC (OPPO/Realme), Dash/Warp Charging (OnePlus), Samsung Adaptive Fast Charging, and Apple 20W PD fast charging.'
    },
    {
      q: 'Are RSD data cables tested for quality?',
      a: 'Yes, 100% of RSD data cables undergo rigorous quality testing including: 10,000+ bend cycle durability test, fast charging protocol verification, electrical safety testing, connector pull-force testing, and final QC inspection. Our ISO 9001:2015 certified facility ensures consistent quality.'
    },
    {
      q: 'How quickly can RSD deliver bulk cable orders?',
      a: 'Standard delivery timeline is 3-5 business days for ready stock items across India. For custom OEM orders, production typically takes 15-20 days followed by shipping. We partner with trusted logistics providers for safe and timely delivery.'
    },
    {
      q: 'What makes RSD different from other cable manufacturers?',
      a: 'RSD Accessories stands out due to: 8+ years of manufacturing expertise, ISO 9001:2015 certification, in-house quality testing lab, support for all fast charging protocols (45W-120W), flexible MOQ for partners, competitive B2B pricing, and dedicated customer support team.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Data Cable Manufacturer in India | RSD Accessories - #1 USB Cable Factory</title>
        <meta name="description" content="RSD Accessories - India's best data cable manufacturer in Noida. ISO certified, 50,000+ daily production. USB Type-C, Micro USB, Lightning cables. OEM manufacturing. Wholesale prices." />
        <meta name="keywords" content="best data cable manufacturer india, data cable manufacturer, usb cable factory india, type c cable manufacturer, fast charging cable manufacturer, bulk cable supplier india, oem cable manufacturer, wholesale data cables" />
        <link rel="canonical" href="https://rsdaccessories.com/best-data-cable-manufacturer-india" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-black py-20 md:py-28">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FF4D00" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <span className="inline-block bg-[#FF4D00] text-white px-4 py-1 rounded-full text-sm font-semibold mb-6">
                #1 Data Cable Manufacturer in India
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                Best Data Cable Manufacturer in <span className="text-[#FF4D00]">India</span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto mb-8">
                RSD Accessories Pvt Ltd - ISO 9001:2015 Certified Factory in Noida with 50,000+ Daily Production Capacity
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#FF4D00] hover:bg-[#E64500] text-white px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Request Bulk Quote
                  </Button>
                </Link>
                <Link to="/shop/data-cables">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                    View Cable Range
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-neutral-100 border-y border-neutral-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">50,000+</span>
                <span className="text-sm text-neutral-600">Daily Production</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">500+</span>
                <span className="text-sm text-neutral-600">Dealers Pan India</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">8+</span>
                <span className="text-sm text-neutral-600">Years Experience</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">ISO</span>
                <span className="text-sm text-neutral-600">9001:2015 Certified</span>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                  Why RSD is India's Best Data Cable Manufacturer
                </h2>
                <p className="text-lg text-neutral-600 mb-6">
                  Since 2016, RSD Accessories Pvt Ltd has been at the forefront of data cable manufacturing in India. Our state-of-the-art facility in Noida, Delhi NCR is equipped with advanced machinery and staffed by skilled technicians who ensure every cable meets international quality standards.
                </p>
                <p className="text-lg text-neutral-600 mb-6">
                  As a B2B wholesale manufacturer, we serve distributors, wholesalers, e-commerce sellers, and OEM partners across India. Our commitment to quality, competitive pricing, and reliable delivery has made us the preferred choice for 500+ dealers nationwide.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#FF4D00] mt-1 flex-shrink-0" />
                      <span className="text-neutral-700">{feature.title}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Our Manufacturing Capabilities</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <Factory className="w-6 h-6" />
                    <span>25,000+ sq. ft. manufacturing facility</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Package className="w-6 h-6" />
                    <span>8 dedicated production lines</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Users className="w-6 h-6" />
                    <span>100-150 skilled workforce</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Shield className="w-6 h-6" />
                    <span>In-house quality testing lab</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Zap className="w-6 h-6" />
                    <span>45W to 120W cable production</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cable Types */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Data Cables We Manufacture
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                Premium quality cables supporting all fast charging protocols
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cableTypes.map((cable, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-neutral-100">
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{cable.name}</h3>
                  <span className="inline-block bg-[#FF4D00]/10 text-[#FF4D00] px-3 py-1 rounded-full text-sm font-semibold mb-4">
                    {cable.power}
                  </span>
                  <ul className="space-y-2">
                    {cable.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-neutral-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Why Choose RSD for Data Cable Manufacturing
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex gap-4 p-6 rounded-xl bg-white border border-neutral-200 hover:border-[#FF4D00] transition-colors">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#FF4D00]/10 rounded-lg flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-[#FF4D00]" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-900 mb-1">{feature.title}</h3>
                    <p className="text-neutral-600">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-neutral-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-neutral-400">
                Common questions about data cable manufacturing in India
              </p>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-neutral-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-3">{faq.q}</h3>
                  <p className="text-neutral-300">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Partner with India's Best Data Cable Manufacturer
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get wholesale prices, OEM services, and reliable Pan India delivery
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/become-distributor">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg">
                  Become a Distributor
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BestDataCableManufacturerIndia;
